import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Avatar,
  Dropdown,
  message,
  Row,
  Col,
  Divider
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  SaveOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

export default function Profile({ role }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone,
        employee_id: user.employee_id,
      });
    }
  }, [user, form]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getDashboardPath = () => {
    switch (role) {
      case 'super_admin':
        return '/super-admin/dashboard';
      case 'admin':
        return '/admin/dashboard';
      case 'facilitator':
        return '/facilitator/dashboard';
      case 'student':
        return '/student/dashboard';
      default:
        return '/dashboard';
    }
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate(getDashboardPath()),
    },
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate(`/${role}/profile`),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleUpdateProfile = async (values) => {
    setLoading(true);
    try {
      const response = await api.put('/users/profile', values);
      message.success('Profile updated successfully');
      // Update user context if needed
      if (response.data) {
        // You might want to update the user context here
      }
    } catch (error) {
      message.error('Failed to update profile');
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    if (values.new_password !== values.confirm_password) {
      message.error('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users/change-password', {
        current_password: values.current_password,
        new_password: values.new_password,
      });
      message.success('Password changed successfully');
      passwordForm.resetFields();
    } catch (error) {
      message.error('Failed to change password');
      console.error('Error changing password:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplay = () => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'facilitator':
        return 'Facilitator';
      case 'student':
        return 'Student';
      default:
        return role;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate(getDashboardPath())}>
            USSEDU Platform
          </Title>
          <Text type="secondary">My Profile</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>My Profile</Title>
          <Text type="secondary">Manage your personal information and preferences</Text>
        </div>

        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card title="Personal Information" style={{ marginBottom: 24 }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleUpdateProfile}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="first_name"
                      label="First Name"
                      rules={[{ required: true, message: 'Please enter your first name' }]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="First Name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="last_name"
                      label="Last Name"
                      rules={[{ required: true, message: 'Please enter your last name' }]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="Last Name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="Email" disabled />
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                    >
                      <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="employee_id"
                      label="Employee ID"
                    >
                      <Input prefix={<IdcardOutlined />} placeholder="Employee ID" disabled />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                    Save Changes
                  </Button>
                </Form.Item>
              </Form>
            </Card>

            <Card title="Change Password">
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handleChangePassword}
              >
                <Form.Item
                  name="current_password"
                  label="Current Password"
                  rules={[{ required: true, message: 'Please enter your current password' }]}
                >
                  <Input.Password placeholder="Current Password" />
                </Form.Item>

                <Form.Item
                  name="new_password"
                  label="New Password"
                  rules={[
                    { required: true, message: 'Please enter a new password' },
                    { min: 8, message: 'Password must be at least 8 characters' }
                  ]}
                >
                  <Input.Password placeholder="New Password" />
                </Form.Item>

                <Form.Item
                  name="confirm_password"
                  label="Confirm New Password"
                  rules={[
                    { required: true, message: 'Please confirm your new password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('new_password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="Confirm New Password" />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Change Password
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <Avatar size={100} style={{ backgroundColor: '#faad14', marginBottom: 16 }}>
                  <Text style={{ fontSize: 36 }}>
                    {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                  </Text>
                </Avatar>
                <Title level={4}>{user?.first_name} {user?.last_name}</Title>
                <Text type="secondary">{user?.email}</Text>
                <Divider />
                <div style={{ textAlign: 'left' }}>
                  <p><Text strong>Role:</Text> <Text>{getRoleDisplay()}</Text></p>
                  <p><Text strong>Employee ID:</Text> <Text>{user?.employee_id || 'N/A'}</Text></p>
                  <p><Text strong>Status:</Text> <Text>Active</Text></p>
                  <p><Text strong>Member Since:</Text> <Text>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</Text></p>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}